import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays, Users, Calculator, WalletCards, ClipboardList, Link2,
  Check, MessageCircle, ShieldCheck, Smartphone, Clock3, BadgeDollarSign,
  ArrowRight, Sparkles, PlayCircle
} from "lucide-react";

const CHECKOUT_URL = process.env.REACT_APP_CHECKOUT_URL || "https://pay.wiapy.com/Er9x4ScF1tE3";
const DEMO_VIDEO_URL = process.env.REACT_APP_DEMO_VIDEO_URL || "/VID_20260930_042520.mp4";

const features = [
  {
    icon: CalendarDays,
    title: "Agenda organizada",
    text: "Cadastre atendimentos, acompanhe horários e dê baixa quando o serviço for concluído.",
  },
  {
    icon: Users,
    title: "Clientes em um só lugar",
    text: "Contato, histórico, observações, aniversários e informações importantes sempre à mão.",
  },
  {
    icon: Calculator,
    title: "Precificação inteligente",
    text: "Considere materiais, tempo, custos, taxas e margem para chegar a uma referência de preço.",
  },
  {
    icon: WalletCards,
    title: "Financeiro sem confusão",
    text: "Acompanhe receitas, despesas, faturamento e resultado do seu negócio.",
  },
  {
    icon: ClipboardList,
    title: "Anamnese organizada",
    text: "Mantenha informações importantes das clientes registradas dentro do sistema.",
  },
  {
    icon: Link2,
    title: "Autoagendamento",
    text: "Envie seu link e deixe a cliente escolher serviço, dia e horário disponível.",
  },
];

function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-9 h-9 rounded-xl bg-[#682F3E] flex items-center justify-center">
        <Sparkles size={18} className="text-[#EBD8D2]" />
      </div>
      <div>
        <div className="font-display font-extrabold text-[#27272A] leading-none">NailControl</div>
        <div className="text-[9px] uppercase tracking-[0.18em] text-[#A15E6D] mt-1">Controle para Nail Designers</div>
      </div>
    </div>
  );
}

function CheckoutButton({ children, className = "" }) {
  const onClick = () => {
    if (CHECKOUT_URL) {
      window.location.href = CHECKOUT_URL;
      return;
    }
    document.getElementById("oferta")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-2xl bg-[#682F3E] text-white font-bold px-6 py-4 hover:bg-[#542431] transition shadow-[0_12px_35px_rgba(104,47,62,0.22)] ${className}`}
    >
      {children} <ArrowRight size={18} />
    </button>
  );
}


function DemoVideo() {
  const [src, setSrc] = useState(DEMO_VIDEO_URL || "");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (DEMO_VIDEO_URL) return undefined;

    let active = true;
    let objectUrl = "";

    (async () => {
      try {
        const urls = Array.from(
          { length: 8 },
          (_, i) => `/demo-video/part${String(i).padStart(2, "0")}.txt`
        );

        const parts = await Promise.all(
          urls.map(async (url) => {
            const response = await fetch(url);
            if (!response.ok) throw new Error("Não foi possível carregar a demonstração.");
            return response.text();
          })
        );

        const base64 = parts.join("").replace(/\s/g, "");
        const binary = window.atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
          bytes[i] = binary.charCodeAt(i);
        }

        objectUrl = URL.createObjectURL(new Blob([bytes], { type: "video/mp4" }));
        if (active) setSrc(objectUrl);
      } catch {
        if (active) setFailed(true);
      }
    })();

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  if (src) {
    return (
      <div className="relative mx-auto w-[225px] sm:w-[255px] md:w-[275px]">
        <div className="absolute -inset-6 rounded-[48px] bg-[#EBD8D2]/70 blur-3xl" />
        <div className="relative overflow-hidden rounded-[40px] border-[8px] border-[#242326] bg-black shadow-[0_30px_80px_rgba(63,31,39,.26)]">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10 h-5 w-24 rounded-b-2xl bg-[#242326]" />
          <video
            className="block w-full aspect-[160/354] object-cover bg-black"
            src={src}
            autoPlay
            muted
            loop
            playsInline
            controls
            preload="auto"
            aria-label="Demonstração real do NailControl em funcionamento"
          >
            Seu navegador não suporta reprodução de vídeo.
          </video>
        </div>
        <div className="mt-3 text-center text-xs text-[#817A7D]">
          Demonstração real do NailControl em ambiente de exemplo.
        </div>
      </div>
    );
  }

  return (
    <div className="relative mx-auto w-[225px] sm:w-[255px] md:w-[275px]">
      <div className="absolute -inset-6 rounded-[48px] bg-[#EBD8D2]/70 blur-3xl" />
      <div className="relative aspect-[160/354] rounded-[40px] border-[8px] border-[#242326] bg-gradient-to-br from-[#38252B] to-[#682F3E] shadow-[0_30px_80px_rgba(63,31,39,.26)] flex flex-col items-center justify-center text-center px-5 text-white">
        <div className="w-16 h-16 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
          <PlayCircle size={34} />
        </div>
        <div className="font-display text-xl font-extrabold mt-4">
          {failed ? "Demonstração indisponível" : "Carregando demonstração…"}
        </div>
        <div className="text-sm text-[#EBD8D2] mt-2">
          {failed ? "Atualize a página para tentar novamente." : "Só um instante."}
        </div>
      </div>
    </div>
  );
}

function TestimonialArtwork() {
  const [src, setSrc] = useState("");

  useEffect(() => {
    let active = true;
    let objectUrl = "";

    (async () => {
      try {
        const urls = [
          "/testimonial-image/part00a.txt",
          "/testimonial-image/part00b.txt",
          "/testimonial-image/part01.txt",
          "/testimonial-image/part02.txt",
          "/testimonial-image/part03.txt",
        ];

        const parts = await Promise.all(
          urls.map(async (url) => {
            const response = await fetch(url);
            if (!response.ok) throw new Error("Não foi possível carregar a arte.");
            return response.text();
          })
        );

        const binary = window.atob(parts.join("").replace(/\s/g, ""));
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
          bytes[i] = binary.charCodeAt(i);
        }

        objectUrl = URL.createObjectURL(new Blob([bytes], { type: "image/png" }));
        if (active) setSrc(objectUrl);
      } catch {
        if (active) setSrc("");
      }
    })();

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  return (
    <div className="overflow-hidden rounded-[28px] border border-[#EEDFDA] bg-[#FAF5F2] shadow-[0_20px_70px_rgba(104,47,62,.10)]">
      {src ? (
        <img
          src={src}
          alt="NailControl na prática — conversas ilustrativas sobre diferentes formas de organizar a rotina"
          className="block w-full h-auto"
          loading="lazy"
        />
      ) : (
        <div className="aspect-[1024/572] bg-[#F5ECE8] animate-pulse" aria-label="Carregando conversas ilustrativas" />
      )}
    </div>
  );
}

function AppPreview() {
  return (
    <div className="relative mx-auto w-[285px] md:w-[330px]">
      <div className="absolute -inset-6 rounded-[48px] bg-[#EBD8D2]/60 blur-3xl" />
      <div className="relative rounded-[42px] border-[9px] border-[#242326] bg-[#FAF8F6] shadow-[0_30px_80px_rgba(63,31,39,0.28)] overflow-hidden">
        <div className="bg-[#242326] w-28 h-6 rounded-b-2xl mx-auto" />
        <div className="px-4 pt-4 pb-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[10px] text-[#A15E6D] font-bold uppercase tracking-wider">NailControl</div>
              <div className="font-display font-bold text-lg text-[#27272A]">Olá, Ana!</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-[#F5ECE8] flex items-center justify-center font-bold text-[#682F3E]">A</div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="rounded-2xl bg-[#F5ECE8] p-3">
              <CalendarDays size={17} className="text-[#A15E6D] mb-2" />
              <div className="text-2xl font-bold text-[#27272A]">4</div>
              <div className="text-[10px] text-[#71717A]">atendimentos hoje</div>
            </div>
            <div className="rounded-2xl bg-[#F5ECE8] p-3">
              <Users size={17} className="text-[#A15E6D] mb-2" />
              <div className="text-2xl font-bold text-[#27272A]">38</div>
              <div className="text-[10px] text-[#71717A]">clientes</div>
            </div>
          </div>

          <div className="rounded-2xl bg-[#682F3E] text-white p-4 mb-4">
            <div className="text-[10px] text-[#EBD8D2]">Faturamento do mês</div>
            <div className="font-display text-2xl font-extrabold mt-1">R$ 2.860,00</div>
          </div>

          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-sm">Próximos atendimentos</span>
            <span className="text-[10px] text-[#A15E6D]">Ver agenda</span>
          </div>

          {[
            ["09:00", "Amanda", "Alongamento"],
            ["11:30", "Juliana", "Manutenção"],
            ["15:00", "Camila", "Nail Art"],
          ].map(([time, name, service]) => (
            <div key={time} className="flex items-center gap-3 py-2.5 border-b border-[#EEE9E6] last:border-0">
              <div className="text-xs font-bold text-[#682F3E] w-10">{time}</div>
              <div className="w-8 h-8 rounded-full bg-[#F5ECE8] flex items-center justify-center text-xs font-bold text-[#A15E6D]">
                {name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold truncate">{name}</div>
                <div className="text-[10px] text-[#71717A] truncate">{service}</div>
              </div>
              <div className="w-2 h-2 rounded-full bg-[#4F7A61]" />
            </div>
          ))}
        </div>
      </div>
      <div className="absolute -left-10 top-28 hidden md:flex bg-white rounded-2xl px-3 py-2 shadow-lg border border-[#EEE9E6] items-center gap-2">
        <Check size={15} className="text-[#4F7A61]" />
        <span className="text-xs font-bold">Tudo organizado</span>
      </div>
      <div className="absolute -right-8 bottom-24 hidden md:flex bg-white rounded-2xl px-3 py-2 shadow-lg border border-[#EEE9E6] items-center gap-2">
        <BadgeDollarSign size={15} className="text-[#682F3E]" />
        <span className="text-xs font-bold">Mais clareza</span>
      </div>
    </div>
  );
}


function ProductShowcase() {
  const cards = [
    {
      icon: CalendarDays,
      title: "Agenda + autoagendamento",
      text: "A cliente escolhe serviço, data e horário pelo link público. O novo atendimento entra na sua agenda.",
      preview: (
        <div className="rounded-2xl border border-[#E7DEDA] bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="font-display text-xl font-extrabold">Agenda</div>
            <div className="rounded-xl bg-[#682F3E] text-white px-3 py-2 text-xs font-bold">+ Novo</div>
          </div>
          <div className="text-xs text-[#817A7D] mt-1">Seus próximos atendimentos</div>
          <div className="mt-4 space-y-2">
            {[
              ["10:00", "Amanda", "Alongamento", "R$ 45,00"],
              ["12:15", "Juliana Martins", "Alongamento", "R$ 45,00"],
              ["15:00", "Camila Souza", "Nail Art Premium", "R$ 55,00"],
            ].map(([time, name, service, price]) => (
              <div key={time} className="rounded-xl bg-[#FBF8F6] border border-[#EEE7E3] p-3 flex items-center gap-3">
                <div className="font-mono-brand font-bold text-[#682F3E] w-11">{time}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{name}</div>
                  <div className="text-[11px] text-[#817A7D] truncate">{service}</div>
                </div>
                <div className="text-xs font-bold">{price}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-xl bg-[#F5ECE8] px-3 py-2 text-xs font-bold text-[#682F3E] flex items-center justify-between">
            <span>Link de autoagendamento</span><Link2 size={15} />
          </div>
        </div>
      ),
    },
    {
      icon: Calculator,
      title: "Precificação inteligente",
      text: "Veja quanto o serviço precisa gerar para pagar o negócio, remunerar seu trabalho e ainda deixar lucro.",
      preview: (
        <div className="relative overflow-hidden rounded-[26px] border border-[#E7DEDA] bg-[#F5ECE8] p-3 flex justify-center">
          <video
            className="block w-full max-w-[270px] aspect-[240/530] object-cover rounded-[20px] bg-black shadow-[0_16px_45px_rgba(104,47,62,.14)]"
            src="/pricing-highlight.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label="Demonstração da precificação do NailControl"
          >
            Seu navegador não suporta reprodução de vídeo.
          </video>
        </div>
      ),
    },
    {
      icon: WalletCards,
      title: "Controle financeiro",
      text: "Acompanhe receitas, despesas e o resultado do mês para entender quanto realmente está sobrando.",
      preview: (
        <div className="rounded-2xl border border-[#E7DEDA] bg-white p-4">
          <div className="grid grid-cols-3 gap-2">
            {[
              ["Receitas", "R$ 14.160", "#4F7A61"],
              ["Despesas", "R$ 5.044", "#B76E56"],
              ["Resultado", "R$ 9.115", "#4F7A61"],
            ].map(([label, value, color]) => (
              <div key={label} className="rounded-xl bg-[#FBF8F6] p-2.5 border border-[#EEE7E3]">
                <div className="text-[9px] uppercase font-bold text-[#817A7D]">{label}</div>
                <div className="font-mono-brand font-bold text-sm mt-1" style={{ color }}>{value}</div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <div className="font-bold text-sm">Movimento financeiro</div>
            <div className="text-[10px] text-[#817A7D]">Receitas e despesas dos últimos meses</div>
            <div className="mt-4 h-28 flex items-end gap-3 px-2">
              {[56, 54, 68, 82, 34, 16].map((h, i) => (
                <div key={i} className="flex-1 flex items-end gap-1">
                  <div className="flex-1 rounded-t-md bg-[#4F7A61]" style={{ height: h }} />
                  <div className="w-2 rounded-t-md bg-[#B76E56]" style={{ height: Math.max(10, h * 0.35) }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      icon: Users,
      title: "Visão geral do negócio",
      text: "Abra o NailControl e veja faturamento, atendimentos, ticket médio e sua agenda do dia em poucos segundos.",
      preview: <AppPreview />,
    },
  ];

  return (
    <section className="max-w-6xl mx-auto px-4 py-16 md:py-24">
      <div className="max-w-3xl mx-auto text-center">
        <div className="text-xs font-extrabold uppercase tracking-[.2em] text-[#A84F67]">Tudo em um só lugar</div>
        <h2 className="font-display text-3xl md:text-5xl font-extrabold mt-3">
          Tudo o que você precisa para organizar seu negócio.
        </h2>
        <p className="text-[#6F696D] text-lg mt-4">
          Agenda, clientes, precificação e financeiro conectados em uma rotina simples para usar todos os dias.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-10">
        {cards.map(({ icon: Icon, title, text, preview }) => (
          <article key={title} className="rounded-[30px] border border-[#EEDFDA] bg-[#FFFDFC] p-5 md:p-6 shadow-[0_18px_55px_rgba(104,47,62,.08)]">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-11 h-11 rounded-2xl bg-[#F5ECE8] flex items-center justify-center text-[#682F3E] shrink-0">
                <Icon size={21} />
              </div>
              <div>
                <h3 className="font-display text-xl font-extrabold">{title}</h3>
                <p className="text-sm text-[#6F696D] mt-1 leading-relaxed">{text}</p>
              </div>
            </div>
            {preview}
          </article>
        ))}
      </div>

      <div className="mt-10 rounded-[28px] bg-[#F5ECE8] border border-[#E9D6CF] px-5 py-7 md:px-8 md:py-8 text-center">
        <div className="font-display text-2xl md:text-3xl font-extrabold text-[#27272A]">
          Tudo isso por <span className="text-[#682F3E]">R$ 29,90</span> uma única vez.
        </div>
        <div className="text-[#6F696D] mt-2">Sem mensalidade nesta oferta de lançamento.</div>
        <CheckoutButton className="mt-5">QUERO MEU NAILCONTROL</CheckoutButton>
      </div>
    </section>
  );
}

export default function Sales() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#FFFDFC] text-[#27272A]">
      <header className="sticky top-0 z-40 bg-[#FFFDFC]/95 backdrop-blur border-b border-[#F0E9E5]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Brand />
          <div className="flex items-center gap-2">
            <button onClick={() => nav("/login")} className="text-sm font-semibold text-[#5F5A5D] px-3 py-2">
              Já tenho conta
            </button>
            <button onClick={() => { window.location.href = CHECKOUT_URL; }} className="hidden sm:inline-flex text-sm font-bold text-[#682F3E] bg-[#F5ECE8] px-4 py-2.5 rounded-xl">
              Ativar acesso
            </button>
          </div>
        </div>
      </header>

      <main>
        <section className="overflow-hidden">
          <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 grid md:grid-cols-[1.1fr_.9fr] gap-10 md:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-[#F5ECE8] text-[#682F3E] px-3 py-1.5 text-xs font-extrabold uppercase tracking-wider">
                <Sparkles size={13} /> Feito para Nail Designers
              </div>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.03] mt-5 tracking-tight">
                Seu negócio não precisa ficar <span className="text-[#A84F67]">na sua cabeça.</span>
              </h1>
              <p className="text-lg md:text-xl text-[#6F696D] mt-5 max-w-2xl leading-relaxed">
                Organize agenda, clientes, preços e financeiro em um único sistema — simples para usar na rotina e pensado para quem trabalha com unhas.
              </p>

              <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:items-center">
                <CheckoutButton>QUERO MEU NAILCONTROL</CheckoutButton>
                <div className="text-sm text-[#6F696D]">
                  <b className="text-[#27272A]">R$ 29,90</b> · pagamento único
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-[#6F696D]">
                <span className="flex items-center gap-1.5"><Check size={15} className="text-[#4F7A61]" /> Sem mensalidade nesta oferta</span>
                <span className="flex items-center gap-1.5"><Check size={15} className="text-[#4F7A61]" /> Uso pelo celular ou computador</span>
                <span className="flex items-center gap-1.5"><Check size={15} className="text-[#4F7A61]" /> Acesso individual</span>
              </div>
            </div>

            <DemoVideo />
          </div>
        </section>

        <section className="bg-[#682F3E] text-white">
          <div className="max-w-6xl mx-auto px-4 py-7 grid sm:grid-cols-3 gap-5 text-center">
            <div><div className="font-display text-2xl font-extrabold">Agenda</div><div className="text-sm text-[#EBD8D2]">horários sob controle</div></div>
            <div><div className="font-display text-2xl font-extrabold">Clientes</div><div className="text-sm text-[#EBD8D2]">histórico organizado</div></div>
            <div><div className="font-display text-2xl font-extrabold">Financeiro</div><div className="text-sm text-[#EBD8D2]">mais clareza no mês</div></div>
          </div>
        </section>

        <ProductShowcase />

        <section className="max-w-6xl mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-xs font-extrabold uppercase tracking-[.2em] text-[#A84F67]">Rotina real</div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold mt-3">Atender é só uma parte. Organizar o estúdio também.</h2>
            <p className="text-[#6F696D] text-lg mt-4">
              Quando cada informação fica em um lugar diferente, você perde tempo procurando o que deveria estar pronto para consultar.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mt-10">
            {[
              ["WhatsApp", "horários, confirmações e contatos espalhados"],
              ["Caderno", "anotações de clientes e retornos difíceis de encontrar"],
              ["Calculadora", "preço definido sem enxergar todos os custos"],
            ].map(([title, text]) => (
              <div key={title} className="rounded-3xl bg-[#F7F0EC] p-6 border border-[#EEDFDA]">
                <div className="text-sm font-extrabold text-[#682F3E]">{title}</div>
                <div className="text-[#6F696D] mt-2">{text}</div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 font-display text-xl md:text-2xl font-bold text-[#682F3E]">
            O NailControl coloca o essencial do seu negócio no mesmo lugar.
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 py-12 md:py-20">
          <TestimonialArtwork />
        </section>

        <section id="oferta" className="max-w-5xl mx-auto px-4 py-16 md:py-24">
          <div className="rounded-[36px] bg-[#682F3E] text-white p-7 md:p-12 text-center shadow-[0_28px_90px_rgba(104,47,62,.2)]">
            <div className="inline-flex rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#F2E4E6]">
              Oferta de lançamento
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold mt-5">Comece a organizar seu negócio hoje.</h2>
            <p className="text-[#EBD8D2] text-lg mt-4 max-w-2xl mx-auto">
              Tenha o NailControl para organizar sua rotina profissional em um só lugar.
            </p>

            <div className="mt-8">
              <div className="text-sm text-[#EBD8D2]">pagamento único de</div>
              <div className="font-display text-6xl md:text-7xl font-extrabold mt-1">R$ 29,90</div>
              <div className="text-sm text-[#EBD8D2] mt-2">sem mensalidade nesta oferta de lançamento</div>
            </div>

            <CheckoutButton className="mt-8 bg-white !text-[#682F3E] hover:!bg-[#FFF7F4] min-w-[260px]">
              QUERO MEU ACESSO
            </CheckoutButton>

            <div className="mt-7 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-[#EBD8D2]">
              <span className="flex items-center gap-1"><ShieldCheck size={15} /> Acesso individual</span>
              <span className="flex items-center gap-1"><Smartphone size={15} /> Online no celular e computador</span>
              <span className="flex items-center gap-1"><MessageCircle size={15} /> WhatsApp facilitado</span>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 pb-20">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-center">Perguntas frequentes</h2>
          <div className="mt-8 divide-y divide-[#ECE3DE] border-y border-[#ECE3DE]">
            <Faq q="Preciso instalar alguma coisa?">
              Não. O NailControl funciona online pelo navegador e pode ser acessado no celular ou no computador.
            </Faq>
            <Faq q="Tem mensalidade?">
              Nesta oferta de lançamento, não. O valor de R$ 29,90 é um pagamento único para o acesso adquirido nesta versão.
            </Faq>
            <Faq q="Como recebo meu acesso?">
              Após a confirmação da compra, você recebe um código individual para criar sua conta no NailControl.
            </Faq>
            <Faq q="Minha cliente precisa ter conta para agendar?">
              Não. Você compartilha seu link público e ela consegue escolher serviço, data e horário disponível sem criar conta.
            </Faq>
            <Faq q="O WhatsApp envia mensagens automaticamente?">
              Nesta versão, o NailControl facilita o acesso ao WhatsApp e oferece modelos de mensagens. Disparos automáticos podem ser disponibilizados futuramente.
            </Faq>
            <Faq q="Novas funções futuras estão incluídas?">
              O acesso cobre os recursos disponibilizados nesta oferta. Recursos, integrações ou planos adicionais lançados futuramente podem ser oferecidos separadamente.
            </Faq>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#ECE3DE] bg-[#FAF7F5]">
        <div className="max-w-6xl mx-auto px-4 py-8 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <Brand />
          <div className="text-xs text-[#817A7D] text-center sm:text-right">
            © {new Date().getFullYear()} NailControl · Plataforma para gestão de Nail Designers
          </div>
        </div>
      </footer>

      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 p-3 bg-white/95 backdrop-blur border-t border-[#ECE3DE]">
        <CheckoutButton className="w-full py-3.5">QUERO POR R$ 29,90</CheckoutButton>
      </div>
    </div>
  );
}

function Faq({ q, children }) {
  return (
    <details className="group py-5">
      <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-bold text-[#27272A]">
        <span>{q}</span>
        <span className="text-[#A15E6D] text-xl group-open:rotate-45 transition">+</span>
      </summary>
      <p className="text-[#6F696D] leading-relaxed mt-3 pr-8">{children}</p>
    </details>
  );
}
